import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { data } from '@portal/pkg-api/shared'
import { AssumptionApi, ChangelogApi, EntityApi, EntityLegacyApi } from '@portal/pkg-api/navigator'
import { stores } from '@migrate/stores'
import UserNotificationStore, { UserNotificationType } from '@migrate/stores/UserNotificationStore'
import i18n from 'i18next'
import { CopyToSubEntityValues } from '@navigator/detail/actionItems/model/CopyToSubEntitySchema'
import { CopyFieldsValues } from '@navigator/detail/actionItems/model/CopyFieldsSchema'
import { ChangelogPage } from '@portal/pkg-api/gen/navigator'
import {
  GenerateMergeDocumentRequest,
  MergeDocumentsResponse,
  ValidateMergeDocumentsResponse,
} from '@portal/pkg-api/gen/legacy'
import { DownloadMergedDocumentsValues } from '@navigator/detail/actionItems/mergeDocuments/model/DownloadMergedDocumentsSchema'

const prefix = 'navigator.navigator'

function addNotificationDeferred(type: UserNotificationType, message: string) {
  setTimeout(() => {
    stores[UserNotificationStore.STORE_NAME].addNotification(type, message)
  }, 0)
}

export const NavigatorQueries = {
  candidateTargetEntities: (entityId: number) => ({
    queryKey: [`${prefix}.getCandidateTargetEntities`, entityId],
    queryFn: () => data(EntityApi.getCandidateTargetEntities(entityId)),
  }),
  mergeDocumentsTemplates: (entityTypeId: number) => ({
    queryKey: [`${prefix}.mergeDocumentsTemplates`, entityTypeId],
    queryFn: async (): Promise<MergeDocumentsResponse[]> => {
      const response = await data(EntityLegacyApi.getMergeDocumentsTemplates(entityTypeId))
      return response.data ?? []
    },
  }),
  mergeDocumentsDownloadList: (entityId: number) => ({
    queryKey: [`${prefix}.mergeDocumentsDownloadList`, entityId],
    queryFn: async (): Promise<MergeDocumentsResponse[]> => {
      const response = await data(EntityLegacyApi.getMergeDocumentsDownloadList(entityId))
      return response.data ?? []
    },
  }),
  changelogEntries: (entityId: number, assumptionId: number) => ({
    queryKey: [`${prefix}.changelogEntries`, { entityId, assumptionId }],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      data(ChangelogApi.entityAssumptionChangelog(entityId, assumptionId, pageParam, 5)),
    getNextPageParam: (lastPage: ChangelogPage) =>
      !lastPage.last ? lastPage.number + 1 : undefined,
    initialPageParam: 0,
    staleTime: 0,
    gcTime: 0,
  }),
}

export function useCopyToSubEntity() {
  return useMutation({
    mutationFn: ({ entityId, entityTypeId, shortName }: CopyToSubEntityValues) =>
      data(EntityApi.copyToSubEntity(entityId, entityTypeId, shortName)),
    onSuccess: ({ error }, variables) => {
      if (!error) {
        stores[UserNotificationStore.STORE_NAME].addNotification(
          UserNotificationType.SUCCESS,
          i18n.t('navigator:detail.copyToSubEntitySuccess', { shortName: variables.shortName }),
        )
      }
    },
  })
}

export const CopyFields = async ({ entityId, targetEntityId }: CopyFieldsValues): Promise<any> => {
  return await data(AssumptionApi.copyEntityValues(entityId, targetEntityId))
}

export function useCopyFields() {
  return useMutation({
    mutationFn: ({ entityId, targetEntityId }: CopyFieldsValues) =>
      data(AssumptionApi.copyEntityValues(entityId, targetEntityId)),
    onSuccess: () => {
      stores[UserNotificationStore.STORE_NAME].addNotification(
        UserNotificationType.SUCCESS,
        i18n.t('navigator:detail.copyFieldsSuccess'),
      )
    },
  })
}

export function useCandidateTargetEntities(entityId: number) {
  return useQuery(NavigatorQueries.candidateTargetEntities(entityId))
}

export function useDownloadMergedDocumentList(entityId: number) {
  return useQuery(NavigatorQueries.mergeDocumentsDownloadList(entityId))
}

export function useMergeDocumentsTemplateList(entityTypeId: number) {
  return useQuery(NavigatorQueries.mergeDocumentsTemplates(entityTypeId))
}

export interface MergeDocumentsValues {
  entityId: number
  templateId: number
  orgId: number
  name: string
}

const emptyValidationResponse: ValidateMergeDocumentsResponse = {
  code: 'error',
  missingTags: [],
  missingValues: [],
}

export const ValidateMergeDocuments = async ({
  entityId,
  templateId,
}: MergeDocumentsValues): Promise<ValidateMergeDocumentsResponse> => {
  try {
    const response = await EntityLegacyApi.validateMergeDocuments(templateId, entityId, {
      mercatusNotifyOnError: false,
    })
    return response.data.data ?? emptyValidationResponse
  } catch (error: any) {
    stores[UserNotificationStore.STORE_NAME].addNotification(
      UserNotificationType.ERROR,
      error.response.data.error,
    )
    return emptyValidationResponse
  }
}

interface MergeDocumentsMutationResponse {
  data: {
    data: MergeDocumentsResponse
  }
}

const mergeDocuments = async ({
  entityId,
  templateId,
  name,
  orgId,
}: MergeDocumentsValues): Promise<MergeDocumentsMutationResponse> => {
  const request: GenerateMergeDocumentRequest = {
    orgId,
    projectId: entityId,
    proposalName: name,
    templateId,
  }

  try {
    return (await EntityLegacyApi.generateMergeDocument(request, {
      mercatusNotifyOnError: false,
    })) as unknown as MergeDocumentsMutationResponse
  } catch (error: any) {
    const message =
      error.response.data.error === 'Document with this name already exists for this project.'
        ? 'navigator:detail.mergeDocumentExists'
        : error.response.data.error
    stores[UserNotificationStore.STORE_NAME].addNotification(
      UserNotificationType.ERROR,
      i18n.t(message),
    )
    throw error
  }
}

export function useMergeDocuments() {
  return useMutation({
    mutationFn: mergeDocuments,
    onSuccess: () => {
      addNotificationDeferred(
        UserNotificationType.SUCCESS,
        i18n.t('navigator:detail.mergeDocumentsSuccess'),
      )
    },
  })
}

export function useDownloadMergedDocuments() {
  return useMutation({
    mutationFn: async ({ entityId, templateId }: DownloadMergedDocumentsValues) => {
      window.open(`/rest/proposal/download/proposal/${entityId}/${templateId}`, '_self')
    },
    onSuccess: () => {
      addNotificationDeferred(
        UserNotificationType.SUCCESS,
        i18n.t('navigator:detail.mergeDocumentDownloadSuccess'),
      )
    },
  })
}

export function useChangelogEntries(entityId: number, assumptionId: number) {
  return useInfiniteQuery(NavigatorQueries.changelogEntries(entityId, assumptionId))
}
